import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authApi } from "@/lib/api/auth";
import { userApi } from "@/lib/api/user";
import { api } from "@/lib/api/client";
import { happyPlaceApi } from "@/lib/api/happyPlace";
import type { User, LoginRequest, RegisterRequest, Gender } from "@/lib/types/auth";
import { useAudienceStore } from "@/lib/stores/audienceStore";
import { useStudyStore } from "@/lib/stores/studyStore";
import type { Audience } from "@/lib/types/audience";

class AudienceMismatchError extends Error {
  userAudience: Audience;
  selectedAudience: Audience;

  constructor(userAudience: Audience, selectedAudience: Audience) {
    super("AUDIENCE_MISMATCH");
    this.name = "AudienceMismatchError";
    this.userAudience = userAudience;
    this.selectedAudience = selectedAudience;
  }
}

const migrateLocalStorageData = async () => {
  try {
    const { useDiaryStore } = await import("@/lib/stores/diaryStore");
    const { usePersonalSourcesStore } = await import(
      "@/lib/stores/personalSourcesStore"
    );
    const { useTreeOfLifeStore } = await import(
      "@/lib/stores/treeOlLifeStore"
    );
    const { useSocialNetworkStore } = await import(
      "@/lib/stores/socialNetworkStore"
    );
    const { useStrengthsMapStore } = await import(
      "@/lib/stores/strengthsMapStore"
    );
    const { useCourseProgressStore } = await import(
      "@/lib/stores/courseProgressStore"
    );

    // Get store instances
    const diaryStore = useDiaryStore.getState();
    const personalSourcesStore = usePersonalSourcesStore.getState();
    const treeOfLifeStore = useTreeOfLifeStore.getState();
    const socialNetworkStore = useSocialNetworkStore.getState();
    const strengthsMapStore = useStrengthsMapStore.getState();
    const courseProgressStore = useCourseProgressStore.getState();

    // Run migrations in parallel
    await Promise.all([
      diaryStore.migrateLocalStorageToBackend(),
      personalSourcesStore.migrateLocalStorageToBackend(),
      treeOfLifeStore.migrateLocalStorageToBackend(),
      socialNetworkStore.migrateLocalStorageToBackend(),
      strengthsMapStore.migrateLocalStorageToBackend(),
      courseProgressStore.migrateLocalStorageToBackend(),
    ]);
  } catch (error) {
    console.error("Failed to migrate localStorage data:", error);
  }
};

const clearCourseDataOnSignOut = async () => {
  try {
    const { useCourseProgressStore } = await import(
      "@/lib/stores/courseProgressStore"
    );
    const { useCourseNotesStore } = await import(
      "@/lib/stores/courseNotesStore"
    );
    useCourseProgressStore.getState().clearLocal();
    useCourseNotesStore.getState().reset();
  } catch (error) {
    console.error("Failed to clear course data on sign-out:", error);
  }
};

let isMigratingGuestDrawings = false;

const migrateGuestDrawingsToBackend = async () => {
  if (isMigratingGuestDrawings) return;
  isMigratingGuestDrawings = true;
  try {
    if (typeof window === "undefined") return;

    const guestKeys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("adult-drawing-")) {
        guestKeys.push(key);
      }
    }

    for (const key of guestKeys) {
      try {
        const data = sessionStorage.getItem(key);
        if (!data) continue;
        const drawing = JSON.parse(data) as {
          name: string;
          imageData: string;
        };
        await happyPlaceApi.createDrawing({
          name: drawing.name,
          imageData: drawing.imageData,
        });
        sessionStorage.removeItem(key);
      } catch (error) {
        console.error("Failed to migrate guest drawing:", error);
      }
    }
  } catch (error) {
    console.error("Failed to migrate guest drawings:", error);
  } finally {
    isMigratingGuestDrawings = false;
  }
};

const syncAudienceFromUser = (user: User | null) => {
  if (!user || !user.audience) {
    return;
  }
  const audienceStore = useAudienceStore.getState();
  audienceStore.setAudience(user.audience);
};

interface AuthStore {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  redirectAfterAuth: string | null;

  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateAudience: (audience: Audience) => Promise<void>;
  updateProfile: (data: { nickname?: string; gender?: Gender | null; audience?: Audience }) => Promise<void>;
  deleteAccount: () => Promise<void>;
  setRedirectAfterAuth: (url: string | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      redirectAfterAuth: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const preSelectedAudience = useAudienceStore.getState().audience;

          const response = await authApi.login(credentials);

          if (
            preSelectedAudience &&
            response.user.audience &&
            response.user.audience !== preSelectedAudience
          ) {
            api.setToken(null);

            const mismatchError = new AudienceMismatchError(
              response.user.audience,
              preSelectedAudience
            );

            set({
              error: "AUDIENCE_MISMATCH",
              isLoading: false,
            });
            throw mismatchError;
          }

          set({
            user: response.user,
            token: response.accessToken,
            isLoading: false,
          });
          api.setToken(response.accessToken);
          useStudyStore.getState().reset();
          syncAudienceFromUser(response.user);

          // Migrate any localStorage data to backend
          migrateLocalStorageData().catch(console.error);
          try {
            await migrateGuestDrawingsToBackend();
          } catch (error) {
            console.error("Failed to migrate guest drawings:", error);
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : "Login failed",
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.register(userData);
          set({
            user: response.user,
            token: response.accessToken,
            isLoading: false,
          });
          api.setToken(response.accessToken);
          useStudyStore.getState().reset();
          syncAudienceFromUser(response.user);

          // Migrate any localStorage data to backend
          migrateLocalStorageData().catch(console.error);
          try {
            await migrateGuestDrawingsToBackend();
          } catch (error) {
            console.error("Failed to migrate guest drawings:", error);
          }
        } catch (error) {
          set({
            error:
              error instanceof Error ? error.message : "Registration failed",
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true, error: null });
        try {
          const currentUser = useAuthStore.getState().user;
          syncAudienceFromUser(currentUser);
          
          await authApi.logout();
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : "Logout failed",
          });
        } finally {
          localStorage.removeItem("strengths-map-sessions");
          localStorage.removeItem("strengths-map-session");
          await clearCourseDataOnSignOut();
          useStudyStore.getState().reset();

          set({
            user: null,
            token: null,
            isLoading: false,
            redirectAfterAuth: null,
          });
        }
      },

      updateAudience: async (audience: Audience) => {
        set({ isLoading: true, error: null });
        try {
          await authApi.updateAudience(audience);
          set((state) => ({
            user: state.user ? { ...state.user, audience } : null,
            isLoading: false,
          }));
          syncAudienceFromUser({ ...useAuthStore.getState().user!, audience });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to update audience",
            isLoading: false,
          });
          throw error;
        }
      },

      updateProfile: async (data: { nickname?: string; gender?: Gender | null; audience?: Audience }) => {
        set({ isLoading: true, error: null });
        try {
          const updatedUser = await userApi.updateProfile(data);
          set((state) => ({
            user: state.user
              ? { 
                  ...state.user, 
                  nickname: updatedUser.nickname,
                  gender: updatedUser.gender ?? null,
                  audience: (updatedUser.audience as Audience) ?? null,
                }
              : null,
            isLoading: false,
          }));
          
          if (data.audience) {
            syncAudienceFromUser({ ...useAuthStore.getState().user!, audience: data.audience });
          }
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to update profile",
            isLoading: false,
          });
          throw error;
        }
      },

      deleteAccount: async () => {
        set({ isLoading: true, error: null });
        try {
          await userApi.deleteAccount();

          localStorage.removeItem("strengths-map-sessions");
          localStorage.removeItem("strengths-map-session");
          await clearCourseDataOnSignOut();
          useStudyStore.getState().reset();

          set({
            user: null,
            token: null,
            isLoading: false,
            redirectAfterAuth: null,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to delete account",
            isLoading: false,
          });
          throw error;
        }
      },

      setRedirectAfterAuth: (url: string | null) =>
        set({ redirectAfterAuth: url }),

      clearError: () => set({ error: null }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        redirectAfterAuth: state.redirectAfterAuth,
      }),
    }
  )
);
